import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/user-feedback/tooltip';
import { AlertTriangle, CheckCircle, Info } from 'lucide-react';

export type SeverityLevel = 'breaking' | 'review' | 'none';

interface SeverityDetails {
  breakingChanges?: any[];
  reviewChanges?: any[];
  addedFields?: any[];
  removedFields?: any[];
  modifiedFields?: any[];
  totalAffectedItems?: number;
}

interface VersionSeverityBadgeProps {
  severity: SeverityLevel;
  details?: SeverityDetails;
  affectedCount?: number;
  showIcon?: boolean;
  className?: string;
}

export function VersionSeverityBadge({
  severity,
  details,
  affectedCount = 0,
  showIcon = true,
  className = ''
}: VersionSeverityBadgeProps) {
  // Configure display based on severity
  const config = getSeverityConfig(severity);

  // Generate tooltip content
  const tooltipContent = generateTooltipContent(severity, details, affectedCount);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            className={`${config.className} ${className} flex items-center gap-1 text-xs cursor-help`}
            variant="outline"
          >
            {showIcon && config.icon}
            <span>{config.label}</span>
            {affectedCount > 0 && (
              <span className="ml-1 font-mono">({affectedCount})</span>
            )}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-sm">
          <div className="space-y-2">
            {tooltipContent}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Get configuration for severity level
 */
function getSeverityConfig(severity: SeverityLevel) {
  switch (severity) {
    case 'breaking':
      return {
        label: 'Breaking',
        className: 'bg-red-50 text-red-700 border-red-200',
        icon: <AlertTriangle className="h-3 w-3" />,
      };
    case 'review':
      return {
        label: 'Review',
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        icon: <Info className="h-3 w-3" />,
      };
    case 'none':
    default:
      return {
        label: 'No Impact',
        className: 'bg-green-50 text-green-700 border-green-200',
        icon: <CheckCircle className="h-3 w-3" />,
      };
  }
}

/**
 * Generate tooltip content based on severity and details
 */
function generateTooltipContent(
  severity: SeverityLevel,
  details?: SeverityDetails,
  affectedCount?: number
) {
  if (!details) {
    return (
      <div className="text-sm">
        {severity === 'breaking' && 'Breaking changes require manual review of affected items.'}
        {severity === 'review' && 'Changes can be reviewed and applied in bulk.'}
        {severity === 'none' && 'No changes or only cosmetic updates.'}
      </div>
    );
  }

  const breakingCount = details.breakingChanges?.length || 0;
  const reviewCount = details.reviewChanges?.length || 0;

  return (
    <>
      <div className="font-semibold text-xs">
        {severity === 'breaking' ? 'Breaking Changes' : severity === 'review' ? 'Review Changes' : 'No Impact'}
      </div>
      
      {affectedCount > 0 && (
        <div className="text-xs text-gray-600">
          {affectedCount} item{affectedCount !== 1 ? 's' : ''} affected
        </div>
      )}

      {breakingCount > 0 && (
        <div className="text-xs">
          <span className="font-medium text-red-600">{breakingCount} breaking change{breakingCount !== 1 ? 's' : ''}</span>
          <ul className="mt-1 ml-3 list-disc list-inside space-y-0.5">
            {details.breakingChanges?.slice(0, 3).map((change, idx) => (
              <li key={idx} className="text-xs">
                {change.fieldLabel || change.fieldId}: {change.reason}
              </li>
            ))}
            {details.breakingChanges && details.breakingChanges.length > 3 && (
              <li className="text-xs italic">...and {details.breakingChanges.length - 3} more</li>
            )}
          </ul>
        </div>
      )}

      {reviewCount > 0 && (
        <div className="text-xs">
          <span className="font-medium text-yellow-600">{reviewCount} review change{reviewCount !== 1 ? 's' : ''}</span>
          <ul className="mt-1 ml-3 list-disc list-inside space-y-0.5">
            {details.reviewChanges?.slice(0, 3).map((change, idx) => (
              <li key={idx} className="text-xs">
                {change.fieldLabel || change.fieldId}: {change.reason}
              </li>
            ))}
            {details.reviewChanges && details.reviewChanges.length > 3 && (
              <li className="text-xs italic">...and {details.reviewChanges.length - 3} more</li>
            )}
          </ul>
        </div>
      )}

      {severity === 'breaking' && (
        <div className="text-xs text-gray-600 pt-1 border-t border-gray-200">
          ⚠️ Manual review required for each affected item
        </div>
      )}

      {severity === 'review' && (
        <div className="text-xs text-gray-600 pt-1 border-t border-gray-200">
          ✓ Can be bulk-updated
        </div>
      )}
    </>
  );
}

export default VersionSeverityBadge;

